<script setup lang="ts">
import { computed } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useAuthStore } from '@/stores'
import FbKidAvatar from '@/components/ui/FbKidAvatar.vue'
import { getAvatarUrl } from '@/utils/imageUrl'

const authStore = useAuthStore()
const route = useRoute()
const router = useRouter()
const pageTitle = computed(() => (route.meta.title as string | undefined) || 'FamBam ⭐')
const backTo = computed(() => typeof route.meta.backTo === 'string' ? route.meta.backTo : undefined)
const backLabel = computed(() => {
  if (backTo.value === undefined) return 'Back'
  const title = router.resolve(backTo.value).meta.title
  return typeof title === 'string' && title ? `Back to ${title}` : 'Back'
})
const avatarUrl = computed(() => getAvatarUrl(authStore.user?.avatar_key))
</script>

<template>
  <v-app-bar color="surface" :elevation="0" class="app-bar" data-testid="app-bar">
    <template v-if="authStore.isAuthenticated && backTo !== undefined" #prepend>
      <v-btn
        icon="mdi-arrow-left"
        variant="text"
        :width="44"
        :height="44"
        :to="backTo"
        data-testid="top-bar-back"
        :aria-label="backLabel"
      />
    </template>

    <v-app-bar-title>
      <h1 class="app-bar__title font-display" data-testid="page-title">{{ pageTitle }}</h1>
    </v-app-bar-title>

    <template v-if="authStore.isAuthenticated" #append>
      <v-btn
        data-testid="top-bar-avatar"
        aria-label="Open settings"
        to="/settings"
        icon
        variant="text"
        :width="44"
        :height="44"
        class="mr-2"
      >
        <FbKidAvatar
          :name="authStore.user?.name ?? ''"
          :user-id="authStore.user?.id ?? ''"
          :image-url="avatarUrl"
        />
      </v-btn>
    </template>
  </v-app-bar>
</template>

<style scoped>
.app-bar::after {
  content: '';
  position: absolute;
  inset-inline: 0;
  bottom: 0;
  height: 2px;
  background-color: rgb(var(--v-theme-outline));
  pointer-events: none;
}

.app-bar__title {
  overflow: hidden;
  font-size: 24px;
  font-weight: 800;
  letter-spacing: -0.02em;
  text-overflow: ellipsis;
  white-space: nowrap;
}
</style>
